// podtemat1_walec_types/TotalSurfaceAreaOfCylinderTask.js
// Pole powierzchni całkowitej walca: Pc = 2πrh + 2πr^2 = 2πr(h + r).
// Akceptujemy wynik dokładny (k*pi) lub liczbowy (±0.01).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import PiInput, { formatPiValueLatex } from "../../../../components/inputs/PiInput";

const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const nearlyAbs = (a, b, eps) => Math.abs(a - b) <= eps;

function parseNumericOrKPi(raw) {
  if (!raw) return null;
  let s = String(raw).trim().replace(",", ".").replace(/\s/g, "");
  if (/^[\+\-]?\d+(\.\d+)?$/.test(s)) return { kind: "numeric", value: Number(s) };
  s = s.replace(/π/gi, "pi");
  let m = s.match(/^\(?([\+\-]?\d+(\.\d+)?)\)?\*?pi$/i); if (m) return { kind: "kpi", k: Number(m[1]) };
  m = s.match(/^pi\*?\(?([\+\-]?\d+(\.\d+)?)\)?$/i);     if (m) return { kind: "kpi", k: Number(m[1]) };
  return null;
}

export function generateTask() {
  const r = pick(range(2, 8));
  const h = pick(range(4, 12));
  const k = 2 * r * (h + r);     // współczynnik przy π w Pc
  const numeric = k * Math.PI;
  return { type: "TotalSurfaceAreaOfCylinderTask", data: { r, h, k, numeric } };
}

export function renderTask({ data }) {
  const { r, h } = data;
  return (
    <div style={{ marginBottom: 12 }}>
      Oblicz <b>pole powierzchni całkowitej</b> walca o promieniu podstawy{" "}
      <InlineMath math={`${r}`} /> i wysokości <InlineMath math={`${h}`} />.
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <PiInput
      value={userAnswer}
      onChange={(ans) => onChange?.(ans)}
      disabled={disabled}
      label="\mathrm{P}_{\mathrm{c}}"
      placeholder="np. 28π, 15*pi, 87,96"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  const parsedFromPi = typeof userAnswer.parsed === "number" ? userAnswer.parsed : null;
  const fallbackParsed = (() => {
    if (!userAnswer?.raw) return null;
    const p = parseNumericOrKPi(userAnswer.raw);
    if (!p) return null;
    return p.kind === "kpi" ? p.k * Math.PI : p.value;
  })();

  const val = Number.isFinite(parsedFromPi) ? parsedFromPi : fallbackParsed;
  if (!Number.isFinite(val)) return false;

  const usedPi = /pi|π/i.test(String(userAnswer.raw || ""));
  const eps = usedPi ? 1e-6 : 0.0100001;
  return nearlyAbs(val, data.numeric, eps);
}

export function renderUserAnswer(userAnswer) {
  const raw = userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const valLatex = formatPiValueLatex(raw) || String(raw);
  return <InlineMath math={`\\displaystyle \\mathrm{P}_{\\mathrm{c}} = ${valLatex}`} />;
}

export function renderAnswer({ data }) {
  const exact = `\\mathrm{P}_{\\mathrm{c}}=${data.k}\\pi`;
  return <InlineMath math={exact} />;
}

export function renderExplanation({ data }) {
  const { r, h, k } = data;
  const twoR = 2 * r;
  const Pb = `${twoR}\\pi\\cdot ${h}=${2 * r * h}\\pi`;
  const Pp = `\\pi\\cdot ${r}^{2}=${r * r}\\pi`;
  const sum = `${2 * r * h}\\pi + 2\\cdot ${r * r}\\pi = ${k}\\pi`;

  return (
    <div style={{ display: "grid", rowGap: 8 }}>
      <p><strong>Krok 1:</strong> Pole powierzchni bocznej:</p>
      <BlockMath math={'{\\mathrm{P}_{\\mathrm{b}}=2\\pi r\\,h='+Pb+'}'} />
      <p><strong>Krok 2:</strong> Pole jednej podstawy:</p>
      <BlockMath math={'{P_{\\text{podst}}=\\pi r^{2}='+Pp+'}'} />
      <p><strong>Krok 3:</strong> Pole całkowite (boczna + dwie podstawy):</p>
      <BlockMath math={'{\\mathrm{P}_{\\mathrm{c}}=\\mathrm{P}_{\\mathrm{b}}+2\\,P_{\\text{podst}}='+sum+'}'} />
    </div>
  );
}
